/**
 * Email validation utilities
 * Provides stricter email validation than Yup's default .email() validator
 */

import * as Yup from 'yup'

/**
 * Stricter email format regex
 * Requires:
 * - At least 1 character before @
 * - At least 1 character for domain name
 * - A dot (.)
 * - At least 2 characters for TLD
 */
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * Create a Yup validator for email addresses with strict format checking
 *
 * This validator is stricter than Yup's built-in .email() validator:
 * - Requires a proper domain with TLD (rejects emails like "user@domain")
 * - Requires at least 2 characters in TLD (rejects "3@hj")
 * - Does not allow whitespace
 *
 * @param message - Error message to show when validation fails
 * @returns Yup string schema with email validation
 *
 * @example
 * const schema = Yup.object({ email: createEmailValidator('Invalid email address') })
 *
 * // Valid emails:
 * // - user@example.com
 * // - name.surname@company.co.uk
 * // - test+tag@domain.org
 *
 * // Invalid emails:
 * // - 3@hj (no dot, TLD too short)
 * // - user@domain (no TLD)
 * // - @example.com (no local part)
 * // - user @example.com (whitespace)
 */
export const createEmailValidator = (message: string) => {
  return Yup.string()
    .transform((value) => {
      // Trim whitespace
      if (typeof value === 'string') {
        return value.trim()
      }
      return value
    })
    .test('email-format', message, (value) => {
      if (!value) return true // Allow empty (use .required() separately)
      return EMAIL_FORMAT.test(value)
    })
}
